import { useCallback } from 'react';
import { useAppData } from '../contexts/AppDataContext';
import { useUIState } from '../contexts/UIStateContext';
import { getCurrentUser } from '../../auth/useUser';
import { requestSettlementSync } from '../../api/cases';
import { addBusinessDays, addCalendarDays, generateSettlementEID, genId, NOTICE_WINDOW_CALENDAR_DAYS } from '../../lib/util';
import type { Case } from '../../domain/initial-cases';

// #543 — THE ONE NOTIFICATION METHOD.
//
// The modal used to offer "Send via API" beside "Send via Email", and the method
// string was the only difference between them. Nothing on the API side delivers
// an API notification: `emailJobForCaseEvent` (apps/api/src/lib/email-producers.ts)
// gates on this exact literal, and no other producer, worker or CTIA call reads
// `notificationMethod` at all. So the API button recorded `notificationMethod`,
// stamped the 30-day notification deadline, opened the 12-business-day cure
// window on an UNSUCCESSFUL decision and wrote a NOTIFICATION_SENT audit row —
// against a notice the registrant never received.
//
// The BCBP describes one notification path: the Vetting and Validation Agent
// emails the Onboarding Platform, which tells the Registrant. There is no
// API-delivered notification in the document.
//
// It is a constant rather than a parameter on purpose: a caller-chosen method
// is what made an undeliverable one expressible. `PATCH /cases/:id` enforces the
// same single value at the edge (`notificationMethod`'s enum in
// apps/api/src/routes/cases.ts), so a non-SPA caller cannot record another one
// either. Change one, change the other — and read the note in email-producers.ts
// before widening this, because a method this app cannot deliver is the bug.
export const NOTIFICATION_METHOD = 'email';

// #693 — WHO A CASE'S NOTICES GO TO, and the SPA's copy of the rule.
//
// `cases.oa_email` was the recipient until this issue. With 382 the Onboarding
// Agent on every case (#692) that field was 382 describing itself, so the
// recipient is now read from the registrant's own submission:
//
//   1. the AUTHORIZER contact — `onboarding.data.authorizerName` /
//      `authorizerEmail`
//   2. the BILLING contact — `onboarding.data.billingContact` / `billingEmail`
//   3. nothing — `null`, and the portal must not offer to send.
//
// CANONICAL SIDE IS THE API: `apps/api/src/lib/notification-contact.ts`. It is
// the side that actually addresses the mail, and this copy exists only so the
// portal can tell an analyst who a notice will reach BEFORE asking the API to
// send it — a second opinion about the recipient would be worse than a copy.
//
// The tie is the disk-read pin in this file's test, "matches the API's
// notification-contact fallback order, and the API applies it": it reads
// `NOTIFICATION_CONTACT_KEYS` out of the API source as text and compares it to
// the constant below, IN ORDER, because the order is the rule. See
// docs/CROSS_PACKAGE_CONSTANTS.md for the convention — and note this pair's
// blind direction: the pin lives in `apps/spa`, whose `spa-changes` detector
// does not match `^apps/api/`, so an API-only pull request never runs it. What
// holds that direction is `notification-contact.test.ts` in the API, which
// spells the two key names out as literals.
export const NOTIFICATION_CONTACT_KEYS: readonly (readonly [string, string])[] = [
  ['authorizerName', 'authorizerEmail'],
  ['billingContact', 'billingEmail'],
];

/** The resolved recipient of a case's notices, or null when there is none. */
export interface NotificationContact {
  name: string;
  email: string;
  source: 'authorizer' | 'billing';
}

// A claim is a non-empty string, trimmed — the same rule as `rawClaim` in
// OnboardingTab.tsx and `isPresentClaim` in the API's routes/cases.ts, except
// that this one returns the trimmed value because an address with surrounding
// whitespace is not deliverable.
function trimmedClaim(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function notificationContact(c: NotificationContactFields): NotificationContact | null {
  const ob = c.onboarding;
  const data = (ob && typeof ob === 'object' && !Array.isArray(ob)
    && (ob as Record<string, unknown>).data) as Record<string, unknown> | undefined;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  for (const [nameKey, emailKey] of NOTIFICATION_CONTACT_KEYS) {
    const email = trimmedClaim(data[emailKey]);
    if (email === null) continue;
    return {
      name: trimmedClaim(data[nameKey]) ?? '',
      email,
      source: emailKey === 'authorizerEmail' ? 'authorizer' : 'billing',
    };
  }
  return null;
}

/** The subset of a case the recipient is resolved from. */
export interface NotificationContactFields {
  onboarding?: unknown;
}

/**
 * The contact's NAME for a list column, falling back to the registrant.
 *
 * Deliberately NOT `notificationContact(c)?.name`. That resolver selects on the
 * ADDRESS, because an address is what a notice needs; a submission naming
 * "Ada Authorizer" with no email yet resolves to null there and would show the
 * registrant in a column that has a perfectly good name to show. A list cell is
 * not a delivery decision, so it walks the same two contacts in the same order
 * and takes the first NAME it finds.
 */
export function notificationContactLabel(
  c: NotificationContactFields & { registrant?: string },
): string {
  const ob = c.onboarding;
  const data = (ob && typeof ob === 'object' && !Array.isArray(ob)
    && (ob as Record<string, unknown>).data) as Record<string, unknown> | undefined;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    for (const [nameKey] of NOTIFICATION_CONTACT_KEYS) {
      const name = trimmedClaim(data[nameKey]);
      if (name !== null) return name;
    }
  }
  return c.registrant || '—';
}

// #543 — THE DECISIONS A VET NOTICE EXISTS FOR, and the one list that says so.
//
// `emailJobForCaseEvent` has a template for SUCCESSFUL and one for UNSUCCESSFUL
// and nothing else; on any other decision it falls off the end and queues no
// mail. So these two are not merely "the usual" decisions — they are the
// complete set for which a notification can be delivered at all.
//
// WHY A LIST RATHER THAN A TRUTHINESS TEST AT EACH CALL SITE. `decision` is a
// nullable enum — 'SUCCESSFUL', 'UNSUCCESSFUL', 'DELETED', null — and five
// places asked `c.decision &&`, which reads as "has been decided" and is not.
// 'DELETED' is truthy. `confirmDeletion` writes it onto a case that may have
// been approved but not yet notified (normal: the 30-day deadline exists
// because the notice is not sent at approval time), and every Send Notification
// trigger then offered a live button on it. Clicking recorded the notice and
// queued nothing — this issue's own defect, through a different door.
//
// The gate below and the buttons read this ONE list, so a sixth call site
// cannot reintroduce the truthiness test without deleting the import.
//
// #589 — THE SERVER KEEPS A COPY, in the `notification=pending` filter in
// apps/api/src/routes/cases.ts, and the two are MECHANICALLY TIED: the
// "matches the API's copy, read out of the API source" pin in lifecycle.test.ts
// reads that file as text and compares, so a change to either side alone fails
// by name. Compared as a SET, because both sides only ask about membership.
// Change one, change the other in the same commit — and see
// docs/CROSS_PACKAGE_CONSTANTS.md for the convention and every other pair.
export const NOTIFIABLE_DECISIONS: readonly string[] = ['SUCCESSFUL', 'UNSUCCESSFUL'];

/** True when a vet notice both exists for this decision and can be delivered. */
export function isNotifiableDecision(decision: string | null | undefined): boolean {
  return decision != null && NOTIFIABLE_DECISIONS.includes(decision);
}

/** The subset of a case that decides whether a recorded notice was deliverable. */
export interface NoticeDeliveryFields {
  notificationSentAt?: string | null;
  notificationMethod?: string | null;
  // #693 — the submission, because the recipient is resolved out of it now.
  onboarding?: unknown;
  decision?: string | null;
  cureDeadline?: string | null;
}

// #543 — "this notice was RECORDED, not DELIVERED", or null when it was fine.
//
// Cases decided before this fix keep whatever they were recorded with, cure
// clocks included: that was decided explicitly, and re-notifying or backfilling
// them would move a regulatory deadline retroactively. What an analyst opening
// one needs instead is to be told, which is what this drives.
//
// The conditions are `emailJobForCaseEvent`'s FOUR refusals, in its order
// (apps/api/src/lib/email-producers.ts) — that function is the authority on
// whether mail was queued and this is a reading of it, not a second opinion.
// If its gates change, change these in the same commit.
//
// READ-TIME vs WRITE-TIME, which is the trap in mirroring that function at all.
//
// The producer decides ONCE, at the moment the notification is written, about
// the row as it is then. This runs later, against a row the lifecycle has moved
// on. So a condition is only safe to mirror if the field it reads is FIXED at
// notification time — otherwise a later step silently rewrites history.
//
//   * `notificationMethod` and `notificationSentAt` move only TOGETHER — set by
//     `sendNotification`, cleared by a post-cure re-decision (#629). Safe.
//   * `cureDeadline` is written by that same PATCH on the UNSUCCESSFUL path;
//     the cure, appeal and re-vet steps move `cureStatus`, not the deadline. Safe.
//   * `decision` is NOT safe: `confirmDeletion` overwrites it with DELETED on a
//     case that may have been notified correctly months earlier. That is why the
//     fourth condition below tests `== null` rather than "not one of the two".
//   * the NOTIFICATION CONTACT is not strictly safe either, and this is
//     disclosed rather than claimed away (#693 moved it from `oaEmail` to the
//     submission and the caveat moved with it, unchanged in force): the
//     Onboarding tab lets an analyst edit the authorizer and billing contacts,
//     so emptying both after a notice went out would make this report a
//     delivered notice as undeliverable. It is kept because there is no routine
//     lifecycle step that clears them — unlike DELETED, which every offboard
//     performs — and dropping the check would lose a defect that IS reachable, a
//     notice recorded with no address at all. If a step is ever added that
//     clears them, this condition has to be narrowed the same way the decision
//     one was.
//
// AND THE SAME CARE AT THE CALL SITES, which is the half this rule was missing.
// A field that is safe to READ here is not automatically safe to GATE ON
// elsewhere: `decision` is a nullable ENUM, never a boolean, and five separate
// places wrote `c.decision &&` meaning "has been decided". 'DELETED' is truthy,
// so all five offered a Send Notification trigger on an offboarded case and the
// narrow read-time guard below could not save them — the record was already
// written. That is why `NOTIFIABLE_DECISIONS` above is a list the call sites
// share rather than a test each of them repeats.
//
// Anyone adding a FIFTH condition: check that its field cannot be rewritten
// after the notification, AND that no call site is treating it as a boolean.
// Say both here.
//
// The fourth is the one that is easy to miss, because it is not an `if`: the
// producer branches on SUCCESSFUL and on UNSUCCESSFUL and then falls off the
// end to a bare `return null`. So a row whose decision is null or DELETED
// queues no mail. That is reachable — `decision`'s own enum in
// apps/api/src/routes/cases.ts admits both, and nothing ties `notificationSentAt`
// to a decision — and without the check here the Overview card would print
// "✓ Registrant notified" for it. Mirroring three of four would also have
// made the paragraph above FALSE, which is worse than never having claimed it.
//
// THE STORED VOCABULARY IS NOT `{email, api}`, which is why this is not a
// two-value test. `apps/api/scripts/seed-demo-cases.ts` INSERTs 'EMAIL',
// 'POSTAL_MAIL' and 'REGISTERED_MAIL' straight against the table, bypassing the
// route, and all three occur. So:
//
//   * email in ANY casing  — delivered by us; the producer case-folds, and an
//     'EMAIL' row is a real notice.
//   * 'api'                — recorded and never delivered by anyone. No API
//     delivery integration ever existed; this is the #543 defect itself.
//   * anything else        — recorded against a route this portal does not
//     operate. A POSTAL_MAIL notice may genuinely have gone out; what is
//     certain is only that WE did not send it, and claiming more than that
//     would be inventing a fact in the opposite direction from the bug.
//
// `method === 'api' ? … : 'delivered'` would label a POSTAL_MAIL case as
// email-delivered, which is the same class of false claim this issue removes.
export function undeliveredNoticeReason(c: NoticeDeliveryFields): string | null {
  // Nothing recorded yet — there is no notice to describe.
  if (!c.notificationSentAt) return null;
  const method = (c.notificationMethod ?? '').toLowerCase();
  if (method !== NOTIFICATION_METHOD) {
    if (method === 'api') {
      return 'Recorded as "api" by a delivery path that never existed — no API notification'
        + ' integration was ever built, so no notice reached the registrant.';
    }
    return `Recorded as "${c.notificationMethod || 'unspecified'}", which is not a path this portal`
      + ' operates — email is the only notice it sends, so this one did not come from here.';
  }
  if (!notificationContact(c)) {
    return 'This case names no notification contact — the submission carries neither an'
      + ' authorizer email nor a billing email — so no notice could be sent.';
  }
  if (c.decision === 'UNSUCCESSFUL' && !c.cureDeadline) {
    return 'No cure deadline was recorded, so the unsuccessful-vet notice could not be rendered or sent.';
  }
  // The producer's fall-through: it has a template for SUCCESSFUL and one for
  // UNSUCCESSFUL and nothing else, so a row it sees with any other decision
  // queues no mail.
  //
  // NARROWED TO "no decision recorded", and NOT to "anything but the two".
  // `DELETED` is deliberately absent — see the drift note above the function.
  // `confirmDeletion` overwrites `decision` on an EXISTING case, and offboarding
  // applies to vetted-SUCCESSFUL registrants, i.e. precisely the ones that WERE
  // properly notified. Flagging those would tell an analyst a notice never
  // arrived when the mail genuinely went out: #543's own defect reversed, and
  // worse in one respect, because it defames a correct record rather than
  // flattering a broken one.
  //
  // The gap accepted in exchange: a caller that records a notice onto an
  // already-DELETED case goes unflagged. That is not reachable from the UI —
  // the modal only opens from a decision, and the deletion path runs after — and
  // it is the safe direction to be wrong in, because the alternative is calling
  // real notices undelivered.
  if (c.decision == null) {
    return 'This notice is recorded against a case with no decision recorded, and there is'
      + ' no notice for one — nothing was sent, and the record claims otherwise.';
  }
  return null;
}

export interface LifecycleActions {
  sendNotification: (caseId: string) => void;
  closeCureWindow: (caseId: string) => void;
  receiveAppeal: (caseId: string) => void;
  recordCure: (caseId: string) => void;
  issueFinalDetermination: (caseId: string, outcome: string) => void;
  initiateReVet: (caseId: string) => void;
  completeReVet: (caseId: string, changed: boolean) => void;
  syncToSettlement: (caseId: string) => void;
  requestDeletion: (caseId: string) => void;
  confirmDeletion: (caseId: string) => void;
  submitChange: (caseId: string, changeType: string, details: string) => void;
  approveChange: (caseId: string, changeId: string) => void;
}

export function useLifecycleActions(): LifecycleActions {
  const { cases, updateCase, mergeLocalCase, showToast } = useAppData();
  const { setModal } = useUIState();

  // #543 — records the notice AND queues the mail, or does neither.
  //
  // No `method` parameter: email is the only path (see NOTIFICATION_METHOD).
  // The recipient guard is the other half of that — `emailJobForCaseEvent`
  // returns null when it can resolve no contact, so without it this write would
  // stamp the 30-day deadline, open the cure window and audit NOTIFICATION_SENT
  // with no mail behind it, which is the same defect the API button was. The
  // vetting-entry gate refuses a case with no notification contact long before a
  // decision exists; this catches the cases that were already decided when it
  // landed. #693 moved the guard from `oaEmail` to the resolved contact.
  const sendNotification = useCallback((caseId: string) => {
    const c = cases.find(x => x.id === caseId)!;
    if (!notificationContact(c)) {
      showToast('No notification contact on this case — fill in the authorizer or billing email'
        + ' on the Onboarding tab before sending the notice.');
      return;
    }
    // #543 — same argument as the contact guard above, different field: a case
    // that cannot be notified must not be recordable as notified. The buttons
    // read `isNotifiableDecision` too, but hiding a button closes nothing — this
    // is the check that actually holds, and it is what keeps the acceptance
    // criterion ("no path in the UI records a notification without queueing
    // mail") true no matter which surface grows a new trigger.
    if (!isNotifiableDecision(c.decision)) {
      showToast(c.decision
        ? `A ${c.decision} case has no vet notice to send — nothing was recorded.`
        : 'This case has no decision yet, so there is nothing to notify the registrant of.');
      return;
    }
    const notifDate = new Date().toISOString();
    const updates: Partial<Case> = {
      notificationSentAt: notifDate,
      notificationMethod: NOTIFICATION_METHOD,
      notificationDeadline: addCalendarDays(c.decisionDate!, NOTICE_WINDOW_CALENDAR_DAYS).toISOString(),
    };
    if (c.decision === 'UNSUCCESSFUL') {
      updates.cureDeadline = addBusinessDays(notifDate, 12).toISOString();
      updates.cureStatus = 'OPEN';
    }
    updateCase(caseId, { ...updates, action: { code: 'NOTIFICATION_SENT', detail: { method: NOTIFICATION_METHOD, decision: c.decision } } });
    if (c.decision === 'UNSUCCESSFUL') updateCase(caseId, { action: { code: 'CURE_WINDOW_OPENED', detail: { businessDays: 12 } } });
    setModal(null);
  }, [cases, updateCase, setModal, showToast]);

  const closeCureWindow = useCallback((caseId: string) => {
    updateCase(caseId, { cureStatus: 'EXPIRED', status: 'CLOSED', action: { code: 'CURE_WINDOW_EXPIRED', detail: {} } });
  }, [updateCase]);

  const receiveAppeal = useCallback((caseId: string) => {
    const appealDate = new Date().toISOString();
    updateCase(caseId, {
      status: 'APPEAL', cureStatus: 'APPEAL_RECEIVED',
      appealReceivedAt: appealDate,
      appealDeadline: addCalendarDays(appealDate, 90).toISOString(),
      action: { code: 'APPEAL_RECEIVED', detail: { reassessmentDays: 90 } },
    });
  }, [updateCase]);

  // #587 — A CURE IS NOT AN APPEAL, and this is the action that says so.
  //
  // Before it there was one staff button, "Record Appeal / Cure Info", and it
  // called `receiveAppeal` for both events. So a registrant who sent the
  // missing document and one who disputed the finding produced the SAME row:
  // `status: 'APPEAL'`, `cureStatus: 'APPEAL_RECEIVED'`, an APPEAL_RECEIVED
  // audit code and a 90-calendar-day appeal clock. `CURED` sat in the PATCH
  // enum (routes/cases.ts) and in the DB CHECK (db/schema/cases.ts) and nothing
  // wrote it. Two consequences, and the second is the larger one: "how many
  // registrants cured?" had no answer in the record at all, and a registrant
  // who merely filled a gap was given a reassessment deadline they had not
  // asked for.
  //
  // WHAT THIS WRITES, and why each field is the value it is:
  //
  //   * `cureStatus: 'CURED'` — the value the schema has always permitted and
  //     nothing produced.
  //
  //   * `status: 'IN_REVIEW'` — THE CASE RESUMES VETTING, which is the whole
  //     difference between the two events. An appeal is a challenge to a
  //     finished decision and gets its own status and its own reassessment
  //     window; a cure is the registrant completing the submission that was
  //     found wanting, so what happens next is the ordinary thing that happens
  //     to an incomplete submission — an analyst vets it. Leaving the status
  //     alone was the alternative and it is worse: the CureAppeals page's Open
  //     Cure Windows section queries `cureStatus: 'OPEN'`, so the row leaves
  //     that section the moment this lands, and with an unchanged status it
  //     would land in no queue at all. IN_REVIEW puts it back in front of an
  //     analyst, which is where a case somebody has to look at belongs.
  //
  //     ONE CONSEQUENCE, disclosed rather than discovered later. Re-entering
  //     IN_REVIEW is a real vetting entry as far as the API is concerned, so
  //     PATCH /cases/:id runs #583's required-field check on it and answers 422
  //     ("Required fields missing") if the onboarding blob is short of one. A
  //     case decided AFTER #583 shipped passed that check on its way in and
  //     passes it again; one decided before it may not, and its analyst gets
  //     the toast rather than a silent write. That is the same exposure the
  //     appeal-outcome and re-vet re-entries named in `slaDeadlineOnVettingEntry`
  //     already carry, and it is the right direction to fail in — a case that
  //     cannot enter vetting should not be recorded as having resumed it.
  //
  //     The SLA clock does not restart: `slaDeadlineOnVettingEntry` refuses to
  //     overwrite a non-null `slaDeadline`, and a case that reached a cure
  //     window has one. A cure buys the registrant another look, not the
  //     programme another fortnight of its own deadline.
  //
  //   * NO `appealDeadline` and NO `appealReceivedAt`. This is the defect
  //     itself, so it is worth saying plainly rather than by omission: nothing
  //     here writes an appeal field, and the 90-day reassessment window (#588,
  //     calendar days) belongs to `receiveAppeal` alone. A registrant who has
  //     not appealed has no appeal to be reassessed.
  //
  //   * `decision` is deliberately NOT cleared, and this is not laziness.
  //     `undeliveredNoticeReason` above reads a null `decision` as "this notice
  //     is recorded against a case with no decision, so nothing was sent" —
  //     clearing it would make the unsuccessful notice that OPENED this cure
  //     window report itself as never delivered. `receiveAppeal` leaves it for
  //     the same reason, and the analyst re-deciding is a separate act with its
  //     own audit code.
  //
  //   * `cureDeadline` is left exactly as it is. It is the date the notice
  //     named, it is a fact about that notice rather than about this case's
  //     current state, and `undeliveredNoticeReason` documents it as one of the
  //     two fields that are FIXED at notification time and therefore safe to
  //     read later. Moving it here would rewrite that history.
  //
  // THE DETAIL NAMES THE REASON CODES, not free text. They are the deficiencies
  // the unsuccessful notice cited — the exact things a cure answers — and the
  // Cure Windows row prints them beside this button, so the audit row and the
  // screen the analyst acted from say the same thing. A typed note would be a
  // second, unvalidated account of the same fact.
  //
  // THE GUARD is what stops this becoming interchangeable with the appeal path
  // again from the other direction. The button is rendered only in the Open
  // Cure Windows section, but hiding a button closes nothing (the #543 lesson,
  // three actions up): `recordCure` is on `LifecycleActions` and any surface can
  // call it, and calling it on a case at `APPEAL_RECEIVED` would overwrite a
  // live appeal with a cure and drop its deadline on the floor. A cure is
  // something that happens to an OPEN cure window or it is not a cure.
  const recordCure = useCallback((caseId: string) => {
    const c = cases.find(x => x.id === caseId);
    if (!c) return;
    if (c.cureStatus !== 'OPEN') {
      showToast(c.cureStatus === 'APPEAL_RECEIVED'
        ? 'This case is under appeal — recording a cure on it would overwrite the appeal. Nothing was recorded.'
        : 'A cure can only be recorded while the cure window is open — nothing was recorded.');
      return;
    }
    updateCase(caseId, {
      status: 'IN_REVIEW', cureStatus: 'CURED',
      action: { code: 'CURE_RECORDED', detail: { reasonCodes: c.reasonCodes ?? [] } },
    });
  }, [cases, updateCase, showToast]);

  const issueFinalDetermination = useCallback((caseId: string, outcome: string) => {
    const user = getCurrentUser()!;
    const fdDate = new Date().toISOString();
    // One atomic PATCH per determination (H-1): the previous two-call shape
    // raced on If-Match and could drop the CHANGED settlement queueing.
    // approverId (Keycloak sub; the API resolves it to users.id) makes the
    // dual_control_distinct_principals CHECK enforce dual control on the
    // appeal path server-side (B-2).
    updateCase(caseId, {
      status: 'FINAL_DETERMINATION',
      finalDetermination: outcome,
      finalDeterminationDate: fdDate,
      approverId: user.id,
      ...(outcome === 'CHANGED' ? {
        decision: 'SUCCESSFUL', status: 'SUCCESSFUL',
        eid: generateSettlementEID(cases.find(x => x.id === caseId)?.registrant),
        revetDueAt: addCalendarDays(fdDate, 365).toISOString(), revetStatus: 'SCHEDULED',
        settlementSyncStatus: 'PENDING_SYNC',
      } : {}),
      action: { code: 'FINAL_DETERMINATION', detail: { outcome } },
    });
  }, [cases, updateCase]);

  const initiateReVet = useCallback((caseId: string) => {
    const reqDate = new Date().toISOString();
    updateCase(caseId, {
      revetStatus: 'REQUESTED', revetRequestedAt: reqDate,
      revetResponseDeadline: addCalendarDays(reqDate, 10).toISOString(),
      action: { code: 'REVET_REQUESTED', detail: { responseDays: 10 } },
    });
  }, [updateCase]);

  const completeReVet = useCallback((caseId: string, changed: boolean) => {
    const newDue = addCalendarDays(new Date(), 365).toISOString();
    // B-3: an unchanged re-vet leaves settlementSyncStatus alone — the prior
    // sync still stands, and only the worker may ever write SYNCED.
    updateCase(caseId, {
      revetStatus: 'COMPLETED', revetDueAt: newDue,
      ...(changed ? { settlementSyncStatus: 'PENDING_SYNC' } : {}),
      action: { code: 'REVET_COMPLETED', detail: { changed } },
    });
  }, [updateCase]);

  // B-3: requests the sync via POST /cases/:id/settlement-sync. The API
  // records SETTLEMENT_SYNC_REQUESTED and enqueues the worker job; the case
  // shows PENDING_SYNC until the worker confirms with the platform and
  // writes SYNCED + SETTLEMENT_SYNC itself.
  const syncToSettlement = useCallback((caseId: string) => {
    requestSettlementSync(caseId)
      .then(() => {
        mergeLocalCase(caseId, { settlementSyncStatus: 'PENDING_SYNC' });
        showToast('Sync requested — status updates when the Settlement Platform confirms', 'success');
      })
      .catch((err: unknown) => { // #802: a problem's detail says why (a 409 = no approval); its title is only "Conflict"
        showToast((err as { detail?: string } | null)?.detail ?? (err instanceof Error ? err.message : 'Settlement sync request failed'));
      });
  }, [mergeLocalCase, showToast]);

  const requestDeletion = useCallback((caseId: string) => {
    updateCase(caseId, { status: 'PENDING_DELETION', action: { code: 'DELETION_REQUESTED', detail: {} } });
  }, [updateCase]);

  // #203 / B-3: no direct settlementSyncStatus write — the API's PATCH route
  // sees decision='DELETED' and enqueues the full offboard (enterprise delete)
  // itself; only the API/worker manage sync status.
  const confirmDeletion = useCallback((caseId: string) => {
    updateCase(caseId, {
      status: 'CLOSED', decision: 'DELETED',
      action: { code: 'ACCOUNT_DELETED', detail: {} },
    });
  }, [updateCase]);

  const submitChange = useCallback((caseId: string, changeType: string, details: string) => {
    const change = { id: genId('CHG'), type: changeType, details, submittedAt: new Date().toISOString(), status: 'PENDING', approvedBy: null, approvedAt: null };
    const c = cases.find(x => x.id === caseId)!;
    updateCase(caseId, {
      pendingChanges: [...(c.pendingChanges || []), change],
      action: { code: 'CHANGE_SUBMITTED', detail: { changeType, details } },
    });
  }, [cases, updateCase]);

  const approveChange = useCallback((caseId: string, changeId: string) => {
    const user = getCurrentUser()!;
    const c = cases.find(x => x.id === caseId)!;
    const now2 = new Date().toISOString();
    const updated = (c.pendingChanges || []).map((ch: any) => ch.id === changeId ? { ...ch, status: 'APPROVED', approvedBy: user.id, approvedAt: now2 } : ch);
    const approved = updated.find((ch: any) => ch.id === changeId);
    updateCase(caseId, {
      pendingChanges: updated.filter((ch: any) => ch.status === 'PENDING'),
      changeHistory: [...(c.changeHistory || []), approved],
      settlementSyncStatus: 'PENDING_SYNC',
      settlementChangeDeadline: addCalendarDays(now2, 1).toISOString(),
      action: { code: 'CHANGE_APPROVED', detail: { changeType: approved.type, syncWindowHours: 24 } },
    });
  }, [cases, updateCase]);

  return {
    sendNotification, closeCureWindow, receiveAppeal, recordCure, issueFinalDetermination,
    initiateReVet, completeReVet, syncToSettlement,
    requestDeletion, confirmDeletion,
    submitChange, approveChange,
  };
}
